import { PRODUCT_EVENTS, useAnalytics } from "@repo/analytics";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { useVenue } from "../../shell/VenueContext";
import { buildChannelUrl, buildStandaloneUrl } from "../embed/snippet";
import { useWidgetOrigin } from "../embed/useWidgetOrigin";
import { buildMetaReserveUrl } from "../meta-reserve/deepLink";
import { useBookableTimes } from "../schedule/bookability";
import { NoBookableTimesAlert } from "../schedule/NoBookableTimesAlert";
import { qrMatrix, qrSvgPath, qrViewBoxSize, type QrMatrix } from "./qr";
import { qrPngBlob, qrSvgBlob, saveBlob } from "./qrFile";

const sectionStyle = css({
  display: "flex",
  flexDirection: "column",
  gap: "3",
  p: "4",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "lg",
});
const headingStyle = css({ fontSize: "md", fontWeight: "semibold" });
const hintStyle = css({ fontSize: "sm", color: "fg.muted" });
const buttonStyle = css({
  px: "3",
  py: "1.5",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "md",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
  whiteSpace: "nowrap",
});
const hiddenWhilePrinting = css({ _print: { display: "none" } });

/** A shared link: selectable, so a denied clipboard is never a dead end, with one-tap copy beside it. */
function CopyLink({ label, url, channel }: { label: string; url: string; channel: string }) {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const [copied, setCopied] = useState(false);

  async function copy() {
    // No clipboard outside a secure context: the link stays selectable, and we must not claim a copy.
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      analytics.capture(PRODUCT_EVENTS.bookingLinkCopied, { channel });
    } catch {
      // Clipboard denied/unavailable: the link stays selectable in the field — nothing to surface.
    }
  }

  return (
    <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2" })}>
      <input
        readOnly
        aria-label={label}
        value={url}
        onFocus={(e) => e.target.select()}
        className={css({
          flex: "1",
          minW: "16rem",
          fontFamily: "mono",
          fontSize: "xs",
          px: "2",
          py: "1.5",
          borderWidth: "1px",
          borderColor: "border.default",
          rounded: "md",
          bg: "bg.subtle",
        })}
      />
      <button type="button" onClick={() => void copy()} className={buttonStyle}>
        {copied ? t("firstBooking.copied") : t("firstBooking.copy")}
      </button>
    </div>
  );
}

function QrCode({ matrix, label, size }: { matrix: QrMatrix; label: string; size: string }) {
  const side = qrViewBoxSize(matrix);
  return (
    <svg
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- role="img" is how an inline SVG gets an accessible name.
      role="img"
      aria-label={label}
      viewBox={`0 0 ${side} ${side}`}
      shapeRendering="crispEdges"
      style={{ width: size, height: size, background: "#fff" }}
    >
      <path d={qrSvgPath(matrix)} fill="#000" />
    </svg>
  );
}

/**
 * The sheet `window.print()` puts on paper. While it is mounted the app root is hidden from print, so
 * only the sheet comes out; browsers that return from print() immediately still fire `afterprint`.
 */
function PrintSheet({
  run,
  matrix,
  venueName,
  onDone,
}: {
  /** Each new value prints again, so a browser that never fires `afterprint` can't jam the button. */
  run: number;
  matrix: QrMatrix;
  venueName: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const root = document.getElementById("root");
    root?.classList.add(hiddenWhilePrinting);
    window.addEventListener("afterprint", onDone);
    window.print();
    return () => {
      root?.classList.remove(hiddenWhilePrinting);
      window.removeEventListener("afterprint", onDone);
    };
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- `run` is the trigger: each new value prints again.
  }, [run, onDone]);

  return createPortal(
    <div
      data-testid="qr-print-sheet"
      className={css({
        display: "none",
        _print: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6",
          textAlign: "center",
          pt: "16",
        },
      })}
    >
      <h1 className={css({ fontSize: "3xl", fontWeight: "bold" })}>{venueName}</h1>
      <QrCode matrix={matrix} label={t("firstBooking.qr.alt", { name: venueName })} size="10cm" />
      <p className={css({ fontSize: "xl" })}>{t("firstBooking.qr.printCta")}</p>
    </div>,
    document.body,
  );
}

function Guide({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
      <h4 className={css({ fontSize: "sm", fontWeight: "semibold" })}>{title}</h4>
      {children}
    </div>
  );
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol
      className={css({
        listStyleType: "decimal",
        pl: "5",
        fontSize: "sm",
        display: "flex",
        flexDirection: "column",
        gap: "1",
      })}
    >
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );
}

/**
 * "Get your first booking" — TableBear brings no marketplace traffic, so an owner's own channels are the
 * only source of bookings. The live link, a printable QR code and short guides, each link carrying the
 * `channel` that attributes its bookings. Shown on the wizard's live state and the channels page's
 * `share` tab, so it can always be found again. Deliberately no "place a test booking" prompt: an owner's
 * own booking would count as the venue's first real one (docs/business/metrics.md).
 */
export function FirstBookingPanel({ onFixHours }: { onFixHours?: () => void }) {
  const { t } = useTranslation();
  const { venueId, venue } = useVenue();
  const analytics = useAnalytics();
  const origin = useWidgetOrigin();
  const { bookability } = useBookableTimes(venueId);
  const [printRun, setPrintRun] = useState(0);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const slug = venue.slug;
  const bookingUrl = buildStandaloneUrl({ origin, slug });
  const qrUrl = buildChannelUrl({ origin, slug, channel: "qr" });
  const matrix = useMemo(() => qrMatrix(qrUrl), [qrUrl]);
  const qrLabel = t("firstBooking.qr.alt", { name: venue.displayName });
  const stopPrinting = useMemo(() => () => setPrintRun(0), []);

  async function download(format: "png" | "svg") {
    setDownloadFailed(false);
    try {
      const blob = format === "png" ? await qrPngBlob(matrix) : qrSvgBlob(matrix);
      saveBlob(blob, `qr-${slug}.${format}`);
      analytics.capture(PRODUCT_EVENTS.bookingQrDownloaded, { format });
    } catch {
      setDownloadFailed(true);
    }
  }

  function print() {
    setPrintRun((run) => run + 1);
    analytics.capture(PRODUCT_EVENTS.bookingQrPrinted);
  }

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "4", maxW: "3xl" })}>
      <p className={hintStyle}>{t("firstBooking.intro")}</p>

      {bookability !== null && bookability !== "OK" && (
        <NoBookableTimesAlert venueId={venueId} bookability={bookability} onFixHours={onFixHours} />
      )}

      <section className={sectionStyle}>
        <h3 className={headingStyle}>{t("firstBooking.link.title")}</h3>
        <p className={hintStyle}>{t("firstBooking.link.hint")}</p>
        <CopyLink label={t("firstBooking.link.title")} url={bookingUrl} channel="link" />
      </section>

      <section className={sectionStyle}>
        <h3 className={headingStyle}>{t("firstBooking.qr.title")}</h3>
        <p className={hintStyle}>{t("firstBooking.qr.hint")}</p>
        <QrCode matrix={matrix} label={qrLabel} size="12rem" />
        <div className={css({ display: "flex", flexWrap: "wrap", gap: "2" })}>
          <button type="button" onClick={() => void download("png")} className={buttonStyle}>
            {t("firstBooking.qr.downloadPng")}
          </button>
          <button type="button" onClick={() => void download("svg")} className={buttonStyle}>
            {t("firstBooking.qr.downloadSvg")}
          </button>
          <button type="button" onClick={print} className={buttonStyle}>
            {t("firstBooking.qr.print")}
          </button>
        </div>
        {downloadFailed && (
          <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
            {t("firstBooking.qr.downloadFailed")}
          </p>
        )}
        <p className={css({ fontSize: "xs", color: "fg.muted" })}>
          {t("firstBooking.qr.slugNote")}
        </p>
      </section>

      <section className={sectionStyle}>
        <h3 className={headingStyle}>{t("firstBooking.guides.title")}</h3>
        <Guide title={t("firstBooking.guides.instagram.title")}>
          <Steps
            items={[
              t("firstBooking.guides.instagram.step1"),
              t("firstBooking.guides.instagram.step2"),
              t("firstBooking.guides.instagram.step3"),
            ]}
          />
          <CopyLink
            label={t("firstBooking.guides.instagram.title")}
            url={buildMetaReserveUrl({ origin, slug })}
            channel="meta"
          />
        </Guide>
        <Guide title={t("firstBooking.guides.google.title")}>
          <Steps
            items={[
              t("firstBooking.guides.google.step1"),
              t("firstBooking.guides.google.step2"),
              t("firstBooking.guides.google.step3"),
            ]}
          />
          <CopyLink
            label={t("firstBooking.guides.google.title")}
            url={buildChannelUrl({ origin, slug, channel: "google" })}
            channel="google"
          />
          <p className={css({ fontSize: "xs", color: "fg.muted" })}>
            {t("firstBooking.guides.google.note")}
          </p>
        </Guide>
        <Guide title={t("firstBooking.guides.site.title")}>
          <p className={hintStyle}>{t("firstBooking.guides.site.body")}</p>
          <Link
            to={`/v/${venueId}/settings/channels?tab=widget`}
            className={css({ fontSize: "sm", textDecoration: "underline" })}
          >
            {t("firstBooking.guides.site.cta")}
          </Link>
        </Guide>
      </section>

      {printRun > 0 && (
        <PrintSheet
          run={printRun}
          matrix={matrix}
          venueName={venue.displayName}
          onDone={stopPrinting}
        />
      )}
    </div>
  );
}
